import { call, put, takeLatest, cancelled } from "redux-saga/effects";
import { fetchDrmReportDetails } from "../../service/fetchDrmReportDetails";
import {
  setDrmReportDetailsSuccess,
  setDrmReportDetailsFailure,
  setDrmReportDetailsDownloadSuccess,
  setDrmReportDetailsDownloadFailure,
} from "../reducers/drmReportDetailsSlice";

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const DEFAULT_SORT_FIELD = "serviceScac";

const REPORT_DOWNLOAD_CONFIG = {
  BID_TO_CRNT_RGN: {
    sortBy: "serviceScac",
    columns: ["region", "curCost", "loadsOf13Weeks", "curLineHaulRpm", "curVsBidCost", "orderType", "tyBidVol", "TY_LANE_BID_VOL", "_Broker_", "_Broker_", "laneExpirationDate"],
  },
  BID_TO_CRNT_LN: {
    sortBy: "laneDescription",
    columns: ["region", "originZone", "originZoneDesc", "destinationZone", "destinationZoneDesc", "lane", "carrierManager", "serviceScac", "carrierScac", "scacCount", "serviceDescription", "curAwardPct", "curAwardVol", "curCost", "loadsOf13Weeks", "curLineHaulRpm", "curVsBidCost", "_13 week Lane Loads_", "orderType", "tyBidAwardPct", "tyBidCost", "tyBidVol", "TY_LANE_BID_VOL", "_Broker_", "_Carrier_", "curVBidLoads", "_dest_city_state_", "destinationCountry", "equipments", "mode", "orig_city_state", "active_flag", "laneEffectiveDate", "bucketType" ],
  },
  BID_TO_CRNT_SCAC: {
    sortBy: "serviceScac",
    columns: ["serviceScac", "carrierScac", "serviceDescription", "curAwardPct", "CUR_V_BID_LOAD_PCT", "_BusinessUnit_", "_CarrEff_", "tariffId"],
  },
  BID_TO_CRNT_LN_SCAC: {
    sortBy: "serviceScac",
    columns: ["region", "originZone", "destinationZone", "lane", "serviceScac", "carrierScac", "scacCount", "serviceDescription", "curAwardPct", "curCost", "loadsOf13Weeks", "curLineHaulRpm", "curVsBidCost", "_13 week Lane Loads_", "orderType", "tyBidCost", "tyBidVol", "TY_LANE_BID_VOL", "_Broker_", "Carrier", "dest_city_state", "destinationCountry", "active_flag"],
  },
  OVR_AWR_LN: {
    sortBy: "serviceScac",
    columns: ["region", "originZone", "originZoneDesc", "destinationZone", "destinationZoneDesc", "lane", "carrierManager", "carrierScac", "scacCount", "TotalAward%", "curAwardPct", "curAwardVol", "curCost", "loadsOf13Weeks", "curLineHaulRpm", "curVsBidCost", "_13 week Lane Loads_"],
  },
  UNDR_AWR_LN: {
    sortBy: "serviceScac",
    columns: ["region", "originZone", "originZoneDesc", "destinationZone", "destinationZoneDesc", "lane", "carrierManager", "carrierScac", "scacCount", "TotalAward%", "curAwardPct", "curAwardVol", "curCost", "loadsOf13Weeks", "curLineHaulRpm", "curVsBidCost", "_13 week Lane Loads_"],
  },
  IN_OUT_RPT: {
    sortBy: "serviceScac",
    columns: ["region", "originZone", "originZoneDesc", "OriginZip", "destinationZoneDesc", "_Carrier_"],
  },
  NO_AWR_LN: {
    sortBy: "serviceScac",
    columns: ["region", "originZone", "originZoneDesc", "destinationZone", "destinationZoneDesc", "lane", "carrierManager", "carrierScac", "scacCount", "TotalAward%", "curAwardPct", "curAwardVol", "curCost", "loadsOf13Weeks", "curLineHaulRpm", "curVsBidCost", "_13 week Lane Loads_"],
  },
  AWD_NO_RPM: {
    sortBy: "serviceScac",
    columns: ["region", "originZone", "originZoneDesc", "destinationZone", "destinationZoneDesc", "lane", "carrierManager", "carrierScac", "scacCount", "TotalAward%", "curAwardPct", "curAwardVol", "curCost", "loadsOf13Weeks", "curLineHaulRpm", "curVsBidCost", "_13 week Lane Loads_"],
  },
  AWRD_LD_BY_ORG: {
    sortBy: "serviceScac",
    columns: ["region", "originZone", "lane", "serviceScac", "serviceDescription", "curAwardPct", "curAwardVol", "_Carrier_", "equipments", "mode", ""],
  },
  LST_12_MN_VAL: {
    sortBy: "serviceScac",
    columns: ["serviceScac", "laneDescription", "originZone", "destinationZone"],
  },
  CARRIER_LANE_RATE: {
    sortBy: "serviceScac",
    columns: ["serviceScac", "laneDescription", "originZone", "destinationZone"],
  },
  UNDER_FIVE_AWARD: {
    sortBy: "serviceScac",
    columns: ["serviceScac", "laneDescription", "originZone", "destinationZone"],
  },
  LANES_NEED_MILE_UPDATES: {
    sortBy: "serviceScac",
    columns: ["serviceScac", "laneDescription", "originZone", "destinationZone"],
  },
};

const projectRowsByColumns = (rows, columns) => {
  if (!Array.isArray(columns) || columns.length === 0) {
    return rows;
  }

  return rows.map((row) =>
    columns.reduce((acc, column) => {
      acc[column] = row?.[column] ?? "";
      return acc;
    }, {})
  );
};

const sortRowsByField = (rows, sortBy = DEFAULT_SORT_FIELD) => {
  return [...rows].sort((a, b) => {
    const aValue = String(a?.[sortBy] || "").toLowerCase();
    const bValue = String(b?.[sortBy] || "").toLowerCase();
    return aValue.localeCompare(bValue, undefined, {
      sensitivity: "base",
      numeric: true,
    });
  });
};

export const applyReportDownloadConfig = (
  rows = [],
  { reportCode } = {}
) => {
  const config = reportCode ? REPORT_DOWNLOAD_CONFIG[reportCode] : undefined;
  const projectedRows = projectRowsByColumns(rows, config?.columns);
  const sortField = config?.sortBy || DEFAULT_SORT_FIELD;

  return sortRowsByField(projectedRows, sortField);
};

export const isGroupedKeyValueRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return false;
  }

  return (
    rows.every((row) => isPlainObject(row)) &&
    rows.some((row) => Object.values(row).some(Array.isArray))
  );
};

export const transformGroupedRowsForDownload = (groupedRows = []) => {
  const groupedMap = new Map();

  groupedRows.forEach((row) => {
    if (!isPlainObject(row)) {
      return;
    }

    Object.entries(row).forEach(([key, value]) => {
      const incomingValues = Array.isArray(value) ? value : [value]; 
      const existingValues = groupedMap.get(key) || [];

      const normalizedValues = incomingValues.reduce((acc, item) => {
        if (item === undefined || item === null || item === "") {
          return acc;
        }

        if (isPlainObject(item)) {
          acc.push({ ...item });
          return acc;
        }

        acc.push({ value: item });
        return acc;
      }, []);

      if (normalizedValues.length === 0) {
        return;
      }

      groupedMap.set(key, [...existingValues, ...normalizedValues]);
    });
  });

  const sortedKeys = Array.from(groupedMap.keys()).sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: "base" })
  );

  const flattenedRows = sortedKeys.reduce((acc, key) => {
    const groupedValues = groupedMap.get(key) || [];
    return [...acc, ...groupedValues];
  }, []);

  return flattenedRows;
};

export function* workerGetDrmReportDetails({ payload }) {
  const controller = new AbortController();
  try {
    const {
      pageNum = 1,
      pageSize = 10,
      reqBody = {},
    } = payload || {};

    const response = yield call(fetchDrmReportDetails, {
      pageNum,
      pageSize,
      requestBody: reqBody,
      controller,
    });

    const details = response?.details || {};
    const data = Array.isArray(details?.data) ? details.data : [];

    yield put(
      setDrmReportDetailsSuccess({
        data,
        onPage: details.onPage ?? pageNum,
        pageSize: details.pageSize ?? pageSize,
        totalPages: details.totalPages ?? 0,
        totalRecords: details.totalRecords ?? data.length,
      })
    );
  } catch (error) {
    const message = error?.message || error?.toString() || "Failed to fetch report details";
    yield put(setDrmReportDetailsFailure(message));
  } finally {
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

export function* workerDownloadDrmReportDetails({ payload }) {
  const controller = new AbortController();
  try {
    const {
      reqBody = {},
      totalPages = 0,
      pageSize = 500,
      reportCode,
      fileName = "ReportExport",
    } = payload || {};

    const pagesToFetch = totalPages && totalPages > 0 ? totalPages : 1;
    const aggregatedRows = [];

    for (let page = 1; page <= pagesToFetch; page += 1) {
      const response = yield call(fetchDrmReportDetails, {
        pageNum: page,
        pageSize,
        requestBody: reqBody,
        controller,
      });

      const details = response?.details || {};
      const data = details?.data;

      if (Array.isArray(data)) { 
        aggregatedRows.push(...data);
      } else if (isPlainObject(data)) {
        aggregatedRows.push(data);
      }
    }


    const normalizedRows = isGroupedKeyValueRows(aggregatedRows)
      ? transformGroupedRowsForDownload(aggregatedRows)
      : aggregatedRows;

    const downloadRows = applyReportDownloadConfig(normalizedRows, {
      reportCode,
    });

    yield put(
      setDrmReportDetailsDownloadSuccess({
        rows: downloadRows,
        fileName,
      })
    );
  } catch (error) {
    const message = error?.message || error?.toString() || "Failed to download report";
    yield put(setDrmReportDetailsDownloadFailure(message));
  } finally {
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

function* drmReportDetailsSaga() {
  yield takeLatest(
    "drmReportDetails/getDrmReportDetails",
    workerGetDrmReportDetails
  );

  yield takeLatest(
    "drmReportDetails/downloadDrmReportDetails",
    workerDownloadDrmReportDetails
  );
}

export default drmReportDetailsSaga;
